import React, { memo } from 'react';

import Checkbox from '../../atoms/checkbox';
import EmailVerificationError from '../../atoms/email-verification-error';

import { Images } from '../../../../app-constants';
import { accessibleOnClick } from '../../../../utils/accessible-on-click';
import { supportUrls } from '../../../../utils/urls';
import { redirectWithNewTab } from '../../../../utils/redirect';
import { contactAdmin } from '../../../../utils/contact-admin';
import hasPermission from '../../../../utils/access-control/has-permission';
import { Permissions } from '../../../../utils/access-control/enums/permissions';
import {
  getAdminFirstName,
  getUserFirstName,
} from '../../../../utils/user-details';

type IProps = {
  availableCredits: number;
  requiredCredits: number;
  showVerifyProspectCheckbox?: boolean;
  verifyProspect?: boolean;
  setVerifyProspect?: (value: boolean) => void;

  smartVerificationValueInAdminSettings?: string;
  smartVerification?: boolean;
  setSmartVerification?: (value: boolean) => void;
};

const ImportEmailVerification: React.FC<IProps> = ({
  availableCredits,
  requiredCredits,
  showVerifyProspectCheckbox = false,
  verifyProspect,
  setVerifyProspect,
  smartVerificationValueInAdminSettings,
  smartVerification,
  setSmartVerification,
}) => {
  const handleConfigureClick = () => {
    if (hasPermission(Permissions.ACCOUNT_USER_READ)) {
      redirectWithNewTab('/settings/admin-settings?scrollTo=prospects');
    } else {
      const adminFirstName = getAdminFirstName();
      const userFirstName = getUserFirstName();

      const emailSubject =
        'Request: Update Admin Settings in Saleshandy for Prospect Re-verification';
      const emailBody = `Hey ${adminFirstName}, %0D%0A %0D%0AI'm reaching out regarding the re-verification of prospects. %0D%0A %0D%0ATo ensure already verified prospects are skipped and to avoid unnecessary email verification credit usage, please enable the respective option under Admin Settings. %0D%0A %0D%0AWould be great if you could prioritize this. %0D%0A %0D%0AThanks, %0D%0A${userFirstName}`;

      contactAdmin(emailSubject, emailBody);
    }
  };

  const VerificationInfoText = (days: number) => (
    <>
      <span>Only prospects that haven't been verified in the past</span>{' '}
      <span className="verification-days-label">{days}</span>{' '}
      <span>{days > 1 ? 'days' : 'day'}</span>{' '}
      <span>will go through verification.</span>{' '}
      {hasPermission(Permissions.ACCOUNT_USER_READ) && (
        <span
          {...accessibleOnClick(handleConfigureClick)}
          className="configure-link"
        >
          Configure
        </span>
      )}
    </>
  );

  return (
    <div className="review-section-container mt-2">
      <div className="review-section-title-container">
        <img src={Images.Shield} alt="Shield" />
        <span className="review-section-title">Email verification</span>
      </div>

      {showVerifyProspectCheckbox && (
        <div className="verify-prospect-checkbox">
          <Checkbox
            id="verify-prospect"
            checked={verifyProspect}
            onChange={(checked) => setVerifyProspect(checked)}
          />
          <span
            {...accessibleOnClick(() => {
              setVerifyProspect(!verifyProspect);
            })}
          >
            Start Email verification after Import
          </span>
        </div>
      )}

      <div className="email-verification-desc-container">
        <p>
          <span>
            To ensure high deliverability and avoid spam, verify prospect emails
            before sending emails. Unverified emails can harm your sender
            reputation, increase bounce rate, and your emails may end up in
            spam.
          </span>
          <a
            href={supportUrls.emailVerificationBlog}
            target="_blank"
            rel="noreferrer"
            className="ml-1"
          >
            Learn more
          </a>
        </p>
      </div>

      {verifyProspect && (
        <div className="smart-verification-container">
          <Checkbox
            checked={smartVerification}
            label="Smart Verification"
            onChange={(value) => {
              setSmartVerification?.(value);
            }}
            disabled={!smartVerificationValueInAdminSettings && !hasPermission(Permissions.ACCOUNT_USER_READ)}
          />
          <p>
            {smartVerificationValueInAdminSettings ? (
              VerificationInfoText(Number(smartVerificationValueInAdminSettings))
            ) : (
              <>
                <span>
                  {hasPermission(Permissions.ACCOUNT_USER_READ)
                    ? VerificationInfoText(15)
                    : 'Configure Smart Verification to automatically skip recently verified prospects for this'}
                </span>
                {!hasPermission(Permissions.ACCOUNT_USER_READ) && (
                  <span
                    {...accessibleOnClick(handleConfigureClick)}
                    className="configure-link"
                  >
                    {' '}Contact Owner
                  </span>
                )}
              </>
            )}
          </p>
        </div>
      )}

      {verifyProspect && requiredCredits > availableCredits && (
        <EmailVerificationError
          availableCredits={availableCredits}
          requiredCredits={requiredCredits}
        />
      )}
    </div>
  );
};

export default memo(ImportEmailVerification);
